const getAllAliases = (name, aliases) => {
  const _aliases = [];
  if (name) {
    _aliases.push(name);
  }
  if (aliases) {
    for (const alias of aliases) {
      _aliases.push(alias);
    }
  }
  return _aliases;
};
const getMiddlewareNameWithAliases = (name, aliases) => {
  return "".concat(name || "anonymous").concat(aliases && aliases.length > 0 ? " (a.k.a. ".concat(aliases.join(","), ")") : "");
};
export const constructStack = () => {
  let absoluteEntries = [];
  let relativeEntries = [];
  let identifyOnResolve = false;
  const entriesNameSet = new Set();
  const sort = entries => entries.sort((a, b) => stepWeights[b.step] - stepWeights[a.step] || priorityWeights[b.priority || "normal"] - priorityWeights[a.priority || "normal"]);
  const removeByName = toRemove => {
    let isRemoved = false;
    const filterCb = entry => {
      const aliases = getAllAliases(entry.name, entry.aliases);
      if (aliases.includes(toRemove)) {
        isRemoved = true;
        for (const alias of aliases) {
          entriesNameSet.delete(alias);
        }
        return false;
      }
      return true;
    };
    absoluteEntries = absoluteEntries.filter(filterCb);
    relativeEntries = relativeEntries.filter(filterCb);
    return isRemoved;
  };
  const removeByReference = toRemove => {
    let isRemoved = false;
    const filterCb = entry => {
      if (entry.middleware === toRemove) {
        isRemoved = true;
        for (const alias of getAllAliases(entry.name, entry.aliases)) {
          entriesNameSet.delete(alias);
        }
        return false;
      }
      return true;
    };
    absoluteEntries = absoluteEntries.filter(filterCb);
    relativeEntries = relativeEntries.filter(filterCb);
    return isRemoved;
  };
  const cloneTo = toStack => {
    var _toStack$identifyOnRe;
    absoluteEntries.forEach(entry => {
      toStack.add(entry.middleware, {
        ...entry
      });
    });
    relativeEntries.forEach(entry => {
      toStack.addRelativeTo(entry.middleware, {
        ...entry
      });
    });
    (_toStack$identifyOnRe = toStack.identifyOnResolve) === null || _toStack$identifyOnRe === void 0 || _toStack$identifyOnRe.call(toStack, stack.identifyOnResolve());
    return toStack;
  };
  const expandRelativeMiddlewareList = from => {
    const expandedMiddlewareList = [];
    from.before.forEach(entry => {
      if (entry.before.length === 0 && entry.after.length === 0) {
        expandedMiddlewareList.push(entry);
      } else {
        expandedMiddlewareList.push(...expandRelativeMiddlewareList(entry));
      }
    });
    expandedMiddlewareList.push(from);
    from.after.reverse().forEach(entry => {
      if (entry.before.length === 0 && entry.after.length === 0) {
        expandedMiddlewareList.push(entry);
      } else {
        expandedMiddlewareList.push(...expandRelativeMiddlewareList(entry));
      }
    });
    return expandedMiddlewareList;
  };
  const getMiddlewareList = function () {
    let debug = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    const normalizedAbsoluteEntries = [];
    const normalizedRelativeEntries = [];
    const normalizedEntriesNameMap = {};
    absoluteEntries.forEach(entry => {
      const normalizedEntry = {
        ...entry,
        before: [],
        after: []
      };
      for (const alias of getAllAliases(normalizedEntry.name, normalizedEntry.aliases)) {
        normalizedEntriesNameMap[alias] = normalizedEntry;
      }
      normalizedAbsoluteEntries.push(normalizedEntry);
    });
    relativeEntries.forEach(entry => {
      const normalizedEntry = {
        ...entry,
        before: [],
        after: []
      };
      for (const alias of getAllAliases(normalizedEntry.name, normalizedEntry.aliases)) {
        normalizedEntriesNameMap[alias] = normalizedEntry;
      }
      normalizedRelativeEntries.push(normalizedEntry);
    });
    normalizedRelativeEntries.forEach(entry => {
      if (entry.toMiddleware) {
        const toMiddleware = normalizedEntriesNameMap[entry.toMiddleware];
        if (toMiddleware === undefined) {
          if (debug) {
            return;
          }
          throw new Error("".concat(entry.toMiddleware, " is not found when adding ") + "".concat(getMiddlewareNameWithAliases(entry.name, entry.aliases), " ") + "middleware ".concat(entry.relation, " ").concat(entry.toMiddleware));
        }
        if (entry.relation === "after") {
          toMiddleware.after.push(entry);
        }
        if (entry.relation === "before") {
          toMiddleware.before.push(entry);
        }
      }
    });
    const mainChain = sort(normalizedAbsoluteEntries).map(expandRelativeMiddlewareList).reduce((wholeList, expandedMiddlewareList) => {
      wholeList.push(...expandedMiddlewareList);
      return wholeList;
    }, []);
    return mainChain;
  };
  const stack = {
    add: function (middleware) {
      let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      const {
        name,
        override,
        aliases: _aliases
      } = options;
      const entry = {
        step: "initialize",
        priority: "normal",
        middleware,
        ...options
      };
      const aliases = getAllAliases(name, _aliases);
      if (aliases.length > 0) {
        if (aliases.some(alias => entriesNameSet.has(alias))) {
          if (!override) throw new Error("Duplicate middleware name '".concat(getMiddlewareNameWithAliases(name, _aliases), "'"));
          for (const alias of aliases) {
            const toOverrideIndex = absoluteEntries.findIndex(entry => {
              var _entry$aliases;
              return entry.name === alias || ((_entry$aliases = entry.aliases) === null || _entry$aliases === void 0 ? void 0 : _entry$aliases.some(a => a === alias));
            });
            if (toOverrideIndex === -1) {
              continue;
            }
            const toOverride = absoluteEntries[toOverrideIndex];
            if (toOverride.step !== entry.step || entry.priority !== toOverride.priority) {
              throw new Error("\"".concat(getMiddlewareNameWithAliases(toOverride.name, toOverride.aliases), "\" middleware with ") + "".concat(toOverride.priority, " priority in ").concat(toOverride.step, " step cannot ") + "be overridden by \"".concat(getMiddlewareNameWithAliases(name, _aliases), "\" middleware with ") + "".concat(entry.priority, " priority in ").concat(entry.step, " step."));
            }
            absoluteEntries.splice(toOverrideIndex, 1);
          }
        }
        for (const alias of aliases) {
          entriesNameSet.add(alias);
        }
      }
      absoluteEntries.push(entry);
    },
    addRelativeTo: (middleware, options) => {
      const {
        name,
        override,
        aliases: _aliases
      } = options;
      const entry = {
        middleware,
        ...options
      };
      const aliases = getAllAliases(name, _aliases);
      if (aliases.length > 0) {
        if (aliases.some(alias => entriesNameSet.has(alias))) {
          if (!override) throw new Error("Duplicate middleware name '".concat(getMiddlewareNameWithAliases(name, _aliases), "'"));
          for (const alias of aliases) {
            const toOverrideIndex = relativeEntries.findIndex(entry => {
              var _entry$aliases2;
              return entry.name === alias || ((_entry$aliases2 = entry.aliases) === null || _entry$aliases2 === void 0 ? void 0 : _entry$aliases2.some(a => a === alias));
            });
            if (toOverrideIndex === -1) {
              continue;
            }
            const toOverride = relativeEntries[toOverrideIndex];
            if (toOverride.toMiddleware !== entry.toMiddleware || toOverride.relation !== entry.relation) {
              throw new Error("\"".concat(getMiddlewareNameWithAliases(toOverride.name, toOverride.aliases), "\" middleware ") + "".concat(toOverride.relation, " \"").concat(toOverride.toMiddleware, "\" middleware cannot be overridden ") + "by \"".concat(getMiddlewareNameWithAliases(name, _aliases), "\" middleware ").concat(entry.relation, " ") + "\"".concat(entry.toMiddleware, "\" middleware."));
            }
            relativeEntries.splice(toOverrideIndex, 1);
          }
        }
        for (const alias of aliases) {
          entriesNameSet.add(alias);
        }
      }
      relativeEntries.push(entry);
    },
    clone: () => cloneTo(constructStack()),
    use: plugin => {
      plugin.applyToStack(stack);
    },
    remove: toRemove => {
      if (typeof toRemove === "string") return removeByName(toRemove);else return removeByReference(toRemove);
    },
    removeByTag: toRemove => {
      let isRemoved = false;
      const filterCb = entry => {
        const {
          tags,
          name,
          aliases: _aliases
        } = entry;
        if (tags && tags.includes(toRemove)) {
          const aliases = getAllAliases(name, _aliases);
          for (const alias of aliases) {
            entriesNameSet.delete(alias);
          }
          isRemoved = true;
          return false;
        }
        return true;
      };
      absoluteEntries = absoluteEntries.filter(filterCb);
      relativeEntries = relativeEntries.filter(filterCb);
      return isRemoved;
    },
    concat: from => {
      var _from$identifyOnResol, _from$identifyOnResol2;
      const cloned = cloneTo(constructStack());
      cloned.use(from);
      cloned.identifyOnResolve(identifyOnResolve || cloned.identifyOnResolve() || ((_from$identifyOnResol = (_from$identifyOnResol2 = from.identifyOnResolve) === null || _from$identifyOnResol2 === void 0 ? void 0 : _from$identifyOnResol2.call(from)) !== null && _from$identifyOnResol !== void 0 ? _from$identifyOnResol : false));
      return cloned;
    },
    applyToStack: cloneTo,
    identify: () => {
      return getMiddlewareList(true).map(mw => {
        var _mw$step;
        const step = (_mw$step = mw.step) !== null && _mw$step !== void 0 ? _mw$step : mw.relation + " " + mw.toMiddleware;
        return getMiddlewareNameWithAliases(mw.name, mw.aliases) + " - " + step;
      });
    },
    identifyOnResolve(toggle) {
      if (typeof toggle === "boolean") identifyOnResolve = toggle;
      return identifyOnResolve;
    },
    resolve: (handler, context) => {
      for (const middleware of getMiddlewareList().map(entry => entry.middleware).reverse()) {
        handler = middleware(handler, context);
      }
      if (identifyOnResolve) {
        console.log(stack.identify());
      }
      return handler;
    }
  };
  return stack;
};
const stepWeights = {
  initialize: 5,
  serialize: 4,
  build: 3,
  finalizeRequest: 2,
  deserialize: 1
};
const priorityWeights = {
  high: 3,
  normal: 2,
  low: 1
};